import { createContext, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { getUserCountry, getUserLanguage } from "../redux/user/selectors";

// Create the context
const LocalizationContext = createContext();

// Custom hook to use the localization context
export const useLocalization = () => {
	const context = useContext(LocalizationContext);
	if (!context) {
		throw new Error("useLocalization must be used within a LocalizationProvider");
	}
	return context;
};

// Provider component
export const LocalizationProvider = ({ children }) => {
	const [searchParams] = useSearchParams();

	// Get values from Redux store (these are set by MainScreen)
	const reduxCountry = useSelector(getUserCountry);
	const reduxLanguage = useSelector(getUserLanguage);

	// Get values from URL parameters
	const urlCountry = searchParams.get("country");
	const urlLanguage = searchParams.get("language");

	// State to hold the current localization values
	const [localization, setLocalization] = useState({
		country: null,
		language: "en",
		isFromUrl: false,
		isFromRedux: false,
	});

	// Update localization state when Redux or URL params change
	useEffect(() => {
		const newLocalization = {
			// Prioritize Redux values (which are processed by MainScreen logic)
			// but fall back to URL params if Redux values aren't available yet
			country: reduxCountry || urlCountry || null,
			language: reduxLanguage || urlLanguage || "en",
			isFromUrl: !!(urlCountry || urlLanguage),
			isFromRedux: !!(reduxCountry || reduxLanguage),
		};

		setLocalization(newLocalization);
	}, [reduxCountry, reduxLanguage, urlCountry, urlLanguage]);

	// Helper functions
	const isSpanishSpeaking = () => {
		const { country, language } = localization;
		return language === "es" || (country && ["es", "mx", "pa"].includes(country.toLowerCase()));
	};

	const isGermanSpeaking = () => {
		const { country, language } = localization;
		return language === "de" || (country && ["de", "at", "ch"].includes(country.toLowerCase()));
	};

	const isFrenchSpeaking = () => {
		const { country, language } = localization;
		return language === "fr" || (country && ["fr", "be", "ca", "ch"].includes(country.toLowerCase()));
	};

	const isItalianSpeaking = () => {
		const { country, language } = localization;
		return language === "it" || (country && ["it", "ch", "sm", "va"].includes(country.toLowerCase()));
	};

	const isPortugueseSpeaking = () => {
		const { country, language } = localization;
		return language === "pt" || (country && ["pt", "br", "ao", "mz"].includes(country.toLowerCase()));
	};

	const isSwahiliSpeaking = () => {
		const { country, language } = localization;
		return language === "sw" || (country && ["ke", "tz", "ug", "rw", "bi"].includes(country.toLowerCase()));
	};

	const getEffectiveLanguage = () => {
		const { country, language } = localization;

		// If language is explicitly set and not "en", use it
		if (language && language !== "en") {
			return language;
		}

		// If country is Spanish-speaking, return "es"
		if (country && ["es", "mx", "pa"].includes(country.toLowerCase())) {
			return "es";
		}

		// If country is German-speaking, return "de"
		if (country && ["de", "at", "ch"].includes(country.toLowerCase())) {
			return "de";
		}

		// If country is French-speaking, return "fr"
		if (country && ["fr", "be", "ca"].includes(country.toLowerCase())) {
			return "fr";
		}

		// If country is Italian-speaking, return "it"
		if (country && ["it", "sm", "va"].includes(country.toLowerCase())) {
			return "it";
		}

		// If country is Portuguese-speaking, return "pt"
		if (country && ["pt", "br", "ao", "mz"].includes(country.toLowerCase())) {
			return "pt";
		}

		// If country is Swahili-speaking, return "sw"
		if (country && ["ke", "tz", "ug", "rw", "bi"].includes(country.toLowerCase())) {
			return "sw";
		}

		// Default to English
		return "en";
	};

	const contextValue = {
		// Core values
		country: localization.country,
		language: localization.language,

		// Source information
		isFromUrl: localization.isFromUrl,
		isFromRedux: localization.isFromRedux,

		// Helper functions
		isSpanishSpeaking,
		isGermanSpeaking,
		isFrenchSpeaking,
		isItalianSpeaking,
		isPortugueseSpeaking,
		isSwahiliSpeaking,
		getEffectiveLanguage,

		// Raw URL parameters (for debugging or special cases)
		urlParams: {
			country: urlCountry,
			language: urlLanguage,
		},

		// Redux values (for debugging or special cases)
		reduxValues: {
			country: reduxCountry,
			language: reduxLanguage,
		},
	};

	return <LocalizationContext.Provider value={contextValue}>{children}</LocalizationContext.Provider>;
};

export default LocalizationContext;
